/**
 * Spec 105 — PersonaConfidenceBadge.
 *
 * Renders a persona's confidence grade with a distinct visual per grade so that
 * `hypothesis` reads visibly WEAKER than `survey_grounded` (FR-16). This is the
 * discipline 108/109's confidence cap relies on, so this component is intended
 * to be reused verbatim by the downstream eval surfaces — the grade reads
 * consistently everywhere.
 *
 * Named PersonaConfidenceBadge to avoid collision with the pre-existing
 * statistical-reliability `ConfidenceBadge` (a different concept).
 */
import type { PersonaConfidence } from '../api/personas.js';

/**
 * Spec 230 — restyled onto `.hub-chip` + tone, tone strength following the
 * original ok(strongest)/info(middle)/warn(weakest) ordering. FR-16 requires
 * `hypothesis` to read visibly WEAKER than `survey_grounded`; the tone step
 * down carries most of that, and the dashed border (the one hand-rolled
 * addition here, not part of the `.hub-chip` recipe) keeps the
 * "author-asserted, not measured" cue the original dashed outline gave.
 */
const CONFIG: Record<
  PersonaConfidence,
  { label: string; tone: 'ok' | 'info' | 'warn'; dashed?: boolean; title: string }
> = {
  survey_grounded: {
    label: 'Survey-grounded',
    tone: 'ok',
    title: 'Grounded in survey data (strongest confidence).',
  },
  research_informed: {
    label: 'Research-informed',
    tone: 'info',
    title: 'Informed by research (moderate confidence).',
  },
  hypothesis: {
    label: 'Hypothesis',
    tone: 'warn',
    dashed: true,
    title: 'Author-asserted hypothesis (weakest confidence).',
  },
};

export function PersonaConfidenceBadge({
  confidence,
}: {
  confidence: PersonaConfidence;
}) {
  const c = CONFIG[confidence];
  return (
    <span
      data-testid="persona-confidence-badge"
      data-confidence={confidence}
      title={c.title}
      className={`hub-chip hub-chip--${c.tone}`}
      style={c.dashed ? { border: '1px dashed var(--hub-color-warning)' } : undefined}
    >
      {c.label}
    </span>
  );
}
